import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, 
  Search, 
  FileText, 
  Gavel, 
  Users, 
  ArrowLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  User,
  LogIn
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const KnowledgeBase = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedTerminology, setExpandedTerminology] = useState(null);
  const [expandedProcedure, setExpandedProcedure] = useState(null);

  const terminologies = [
    {
      id: 1,
      title: "Court Case Starter",
      term: "Writ of Summons",
      definition: "A document used to officially start a civil case in court. It tells the other party that a case has been filed against them."
    },
    {
      id: 2,
      title: "Your Claim/Story",
      term: "Statement of Claim",
      definition: "The detailed written story of the person suing (plaintiff) — explaining the facts, what went wrong, and what remedy they want from the court."
    },
    {
      id: 3,
      title: "Your Defence/Reply",
      term: "Statement of Defence",
      definition: "The official reply of the defendant (the one being sued) — explaining why they disagree with the claim, or presenting their own version of events."
    },
    {
      id: 4,
      title: "Witness Statement",
      term: "Witness Statement",
      definition: "A written account from someone who has seen, heard, or knows something important about the case, signed by that person to be used in court."
    },
    {
      id: 5,
      title: "Counter Claim Against Other Party",
      term: "Counterclaim",
      definition: "When the defendant turns around and sues the plaintiff within the same case."
    },
    {
      id: 6,
      title: "Reply to Defence",
      term: "Reply to Defence",
      definition: "The plaintiff's short response to issues raised in the defendant's Statement of Defence."
    },
    {
      id: 7,
      title: "Updated Case Document",
      term: "Amended Pleadings",
      definition: "When any party changes or corrects their earlier filed documents (claim, defence, etc.), with permission from the court."
    },
    {
      id: 8,
      title: "Summary of Claim on Writ",
      term: "Endorsement of Claim",
      definition: "A short version of what the plaintiff is claiming, written directly on the Writ of Summons."
    },
    {
      id: 9,
      title: "Affidavit",
      term: "Affidavit",
      definition: "A written statement of facts, sworn under oath, usually attached to applications or motions in court."
    },
    {
      id: 10,
      title: "Motion/Application",
      term: "Motion/Application",
      definition: "A request made to the court (in writing) asking the judge to make a specific order, usually supported by an affidavit."
    },
    {
      id: 11,
      title: "Court Order",
      term: "Court Order",
      definition: "A written instruction made by the court that must be obeyed (e.g., injunction, judgment, ruling)."
    },
    {
      id: 12,
      title: "Pleadings",
      term: "Pleadings",
      definition: "The formal documents exchanged by both parties in a case (statement of claim, defence, reply, etc.)."
    },
    {
      id: 13,
      title: "Interlocutory Application",
      term: "Interlocutory Application",
      definition: "A request made while the case is still ongoing (not at the end), e.g., asking for temporary injunctions."
    },
    {
      id: 14,
      title: "Execution",
      term: "Execution",
      definition: "Steps taken to enforce a judgment when one party wins (e.g., selling property, garnishee order to collect money)."
    }
  ];

  const procedures = [
    {
      id: 1,
      title: "How to Start a Civil Case",
      steps: [
        "A civil case is started by the plaintiff (person suing).",
        "The main document is the Writ of Summons (Court Case Starter).",
        "The writ must contain a summary of the claim (Endorsement of Claim).",
        "The plaintiff must also prepare a Statement of Claim (Your Claim/Story) and file it with the court.",
        "The court stamps and issues the documents, and they are served on the defendant (official delivery by bailiff)."
      ]
    },
    {
      id: 2,
      title: "How the Defendant Responds",
      steps: [
        "After receiving the writ, the defendant must first enter appearance (tell the court they are aware of the case).",
        "The defendant then files a Statement of Defence (Your Defence/Reply).",
        "If the defendant also has a claim against the plaintiff, they add a Counterclaim.",
        "The plaintiff may then file a Reply to Defence to close the pleadings."
      ]
    },
    {
      id: 3,
      title: "Supporting Documents Along the Way",
      steps: [
        "Witness Statements: Filed before trial to show what each witness will say.",
        "Affidavits: Used to support motions (requests to the court).",
        "Amended Pleadings: If mistakes are made, parties can update their earlier documents (with leave of court)."
      ]
    },
    {
      id: 4,
      title: "Applications (Motions) During the Case",
      steps: [
        "Parties may need temporary orders, e.g.:",
        "• Interlocutory Injunctions (to stop someone from doing something until the case ends).",
        "• Discovery Applications (asking the other side to produce documents).",
        "These are made by filing a Motion with an Affidavit."
      ]
    },
    {
      id: 5,
      title: "Trial Stage",
      steps: [
        "Each side presents their case through their pleadings and witness statements.",
        "Witnesses testify and may be cross-examined.",
        "Lawyers (or parties themselves) argue the law and facts."
      ]
    },
    {
      id: 6,
      title: "Judgment",
      steps: [
        "After hearing the case, the judge gives a judgment (decision).",
        "The judgment may award money, declare rights, or order specific actions."
      ]
    },
    {
      id: 7,
      title: "After Judgment – Enforcement (Execution)",
      steps: [
        "If the losing party does not comply, the winning party may apply for:",
        "• Writ of FiFa (to seize and sell property).",
        "• Garnishee Order (to collect money from the losing party's bank).",
        "• Possession Order (to take back land or buildings)."
      ]
    },
    {
      id: 8,
      title: "Appeals",
      steps: [
        "If unhappy with the judgment, a party may appeal to a higher court within the set time (High Court → Court of Appeal → Supreme Court)."
      ]
    }
  ];

  const filteredTerminologies = terminologies.filter(
    item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProcedures = procedures.filter(
    item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.steps.some(step => step.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-red-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_legaltech-ghana/artifacts/2u2cnqb9_file_000000007aec61f8982bd709fec16f87%20%281%29.png"
                alt="EduLegalHub Logo"
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                EduLegalHub
              </span>
            </Link>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                <ArrowLeft className="h-4 w-4 mr-2 inline" />
                Back to Home
              </Link>
              
              {user ? (
                <div className="flex items-center space-x-2 text-gray-700">
                  <User className="h-4 w-4" />
                  <span className="text-sm font-semibold">{user.name}</span>
                </div>
              ) : (
                <Link to="/" className="flex items-center space-x-2 text-gray-700 hover:text-blue-600">
                  <LogIn className="h-4 w-4" />
                  <span>Sign In</span>
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-4 py-2 mb-6">
              Legal Knowledge Base
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                Legal Knowledge Base
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              Your comprehensive guide to understanding legal terminologies and court procedures in Ghana. 
              Navigate the legal system with confidence using our expert-curated resources.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-md mx-auto mb-8">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search legal terms and procedures..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Navigation Tabs */}
            <div className="flex justify-center space-x-4 mb-12">
              <Button
                onClick={() => setActiveSection('overview')}
                variant={activeSection === 'overview' ? 'default' : 'outline'}
                className="px-6"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Overview
              </Button>
              <Button
                onClick={() => setActiveSection('terminologies')}
                variant={activeSection === 'terminologies' ? 'default' : 'outline'}
                className="px-6"
              >
                <FileText className="h-4 w-4 mr-2" />
                Terminologies
              </Button>
              <Button
                onClick={() => setActiveSection('procedures')}
                variant={activeSection === 'procedures' ? 'default' : 'outline'}
                className="px-6"
              >
                <Gavel className="h-4 w-4 mr-2" />
                Court Procedures
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="pb-20 px-4">
        <div className="container mx-auto">
          
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-blue-800">
                      <FileText className="h-6 w-6 mr-3" />
                      Legal Terminologies
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-700 mb-4">
                      Master essential legal terms and concepts used in Ghanaian courts. 
                      From basic document types to complex legal procedures.
                    </CardDescription>
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">{terminologies.length} Terms</Badge>
                      <Button 
                        onClick={() => setActiveSection('terminologies')}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        Explore Terms
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-pink-50 to-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-pink-800">
                      <Gavel className="h-6 w-6 mr-3" />
                      Court Procedures
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-700 mb-4">
                      Step-by-step guide to understanding how civil cases move through 
                      Ghanaian courts, simplified for everyday users.
                    </CardDescription>
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">{procedures.length} Procedures</Badge>
                      <Button 
                        onClick={() => setActiveSection('procedures')}
                        size="sm"
                        className="bg-pink-500 hover:bg-pink-600"
                      >
                        Learn Process
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-white">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-800">
                    <AlertCircle className="h-6 w-6 mr-3" />
                    How to Use This Knowledge Base
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Search className="h-6 w-6 text-blue-600" />
                      </div>
                      <h4 className="font-semibold mb-2">Search & Discover</h4>
                      <p className="text-sm text-gray-600">Use the search bar to quickly find specific legal terms or procedures</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <BookOpen className="h-6 w-6 text-pink-600" />
                      </div>
                      <h4 className="font-semibold mb-2">Learn & Understand</h4>
                      <p className="text-sm text-gray-600">Read simplified explanations of complex legal concepts and processes</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <h4 className="font-semibold mb-2">Apply Knowledge</h4>
                      <p className="text-sm text-gray-600">Use your understanding to navigate legal processes with confidence</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Terminologies Section */}
          {activeSection === 'terminologies' && (
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-gray-800">Legal Terminologies</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Essential legal terms you need to know when dealing with court cases in Ghana
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTerminologies.map((terminology) => (
                  <Card 
                    key={terminology.id} 
                    className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-0 ${
                      expandedTerminology === terminology.id ? 'bg-gradient-to-br from-blue-50 to-white shadow-lg' : 'bg-white shadow-md'
                    }`}
                    onClick={() => setExpandedTerminology(expandedTerminology === terminology.id ? null : terminology.id)}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-blue-800 flex items-start">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                          <span className="text-xs font-bold text-blue-600">{terminology.id}</span>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 font-normal mb-1">{terminology.title}</div>
                          <div className="text-blue-800">{terminology.term}</div>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    {expandedTerminology === terminology.id && (
                      <CardContent className="pt-0">
                        <p className="text-gray-700 leading-relaxed">{terminology.definition}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>

              {filteredTerminologies.length === 0 && (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Results Found</h3>
                  <p className="text-gray-500">Try adjusting your search terms</p>
                </div>
              )}
            </div>
          )}

          {/* Procedures Section */}
          {activeSection === 'procedures' && (
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-gray-800">Court Procedures</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Step-by-step guide to understanding how civil cases move through Ghanaian courts
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {filteredProcedures.map((procedure) => (
                  <Card 
                    key={procedure.id} 
                    className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-0 ${
                      expandedProcedure === procedure.id ? 'bg-gradient-to-br from-pink-50 to-white shadow-lg' : 'bg-white shadow-md'
                    }`}
                    onClick={() => setExpandedProcedure(expandedProcedure === procedure.id ? null : procedure.id)}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-pink-800 flex items-center">
                        <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-bold text-pink-600">{procedure.id}</span>
                        </div>
                        {procedure.title}
                      </CardTitle>
                    </CardHeader>
                    {expandedProcedure === procedure.id && (
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {procedure.steps.map((step, index) => (
                            <div key={index} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-gray-700 text-sm leading-relaxed">{step}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>

              {filteredProcedures.length === 0 && (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Results Found</h3>
                  <p className="text-gray-500">Try adjusting your search terms</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-pink-600">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Create Your Legal Documents?</h2>
            <p className="text-xl mb-8 text-blue-100">
              Now that you understand the legal process, let our AI help you create professional, court-compliant documents.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 font-semibold"
                asChild
              >
                <Link to="/document-generator">
                  <FileText className="mr-2 h-5 w-5" />
                  Generate Legal Documents
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 font-semibold"
                asChild
              >
                <Link to="/">
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back to Home
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeBase;